import getConfig from 'next/config'
import { Analytics, EventValues } from '@sial/common-utils'
import { groupBy, forEach } from 'lodash'
import { uTagLinkAdapter } from '@utils/tealiumAnalytics'
import { ConfiguratorProductType } from '@utils/analytics/configuratorAnalytics'
import {
  GTMEcommerceItem,
  MaterialProductsData,
  PaClipData,
  PromoProductItems,
} from '@utils/analytics/types'
import {
  AddToCartPagesEnum,
  PaymentMethodValues,
  Dimension91,
} from '@utils/analytics/enums'
import { CartItemInput } from '@src/types/graphql-types'

const {
  publicRuntimeConfig: {
    isTealiumEnabled,
    isGoogleTagManagerEnabled,
    isAnalyticsEnabled: analyticsAreEnabled, // Import under different name to export and use as isAnalyticsEnabled elsewhere.
  },
} = getConfig()

// Globally defines is ANY analytics platform should be used on the site.
export const isAnalyticsEnabled = analyticsAreEnabled

type AnalyticsPayload = {
  eventType?: string // Legacy UA/GA3 field to eventually be removed completely.
  payload: {
    event: string
    ecommerce?: {
      items: object[]
      [key: string]: any
    }
    [key: string]: any
  }
}

export const sendEvent = (props: AnalyticsPayload): void => {
  const { payload, eventType } = props

  const legacyUAFields = [
    'eventCategory',
    'eventAction',
    'eventLabel',
    'event_category',
    'event_action',
    'event_label',
    'eventInteractionType',
  ]

  if (
    eventType ||
    Object.keys(payload).some((key) => legacyUAFields.includes(key))
  ) {
    console.warn(
      `Analytics: It appears the ${payload.event} has legacy analytics fields. Consider removing them.`
    )
  }

  // A bit wonky since GA4 wants an object with only a single property of 'payload' and nothing else. This also makes eventType which triggers GA3 events omitted.
  isGoogleTagManagerEnabled &&
    Analytics.sendEvent(
      { payload: payload },
      isAnalyticsEnabled,
      isGoogleTagManagerEnabled
    )

  // When Tealium is enabled we want to submit the uTagLink event with some Tealium specific payload modifications.
  isTealiumEnabled && uTagLinkAdapter(payload)
}

/***
 * HELPER UTILITY FUNCTIONS AND CONSTANTS
 *
 * There are a number of functions in the analytics files made for simply extracting things like
 * price and currency codes from a line item.
 *
 * As well as a number of regex cleanups for item structure remappings.
 *
 * They'll all be houses below here and are imported as needed throughout other analytics files and the application.
 */

// regex that for stripping encoded unicode code string from product name
export const regexForStripEncodingUnicode = /([&][#](\d+)[;])/g
// regex for stripping html tab e.g <> from product name
export const regexForStripHTML = /(<([^>]+)>)/gi
// regex for stripping special characters that other than letter/space/number from product name
export const regexForStripSpecialChars = /[^\w\s]/gi
// regex for stripping trade mark, copy right symbol etc,
// some case we  have getting symbols instead of encoded unicode in product name
export const regexForStripSymbols = /[™®©]/g
export const regexForStripBrackets = / *\{[^}]*\} */g
export const regexForStripHTMLWithContent = /<.*>.*?/gi
export const regexForStripDashes = /[-]/g

// Tealium uses CNY as default currency, GA uses USD
export const DEFAULT_CURRENCY_CODE = isGoogleTagManagerEnabled ? 'USD' : 'CNY'
export const CART_DATA_STORAGE_KEY = 'CART_DATA_STORAGE_KEY'
export const ADD_TO_CART_GA_DATA_STORAGE_KEY = 'ADD_TO_CART_GA_DATA_STORAGE_KEY'

// Google Analytics accepts only "MXN" unit for Mexico, So changing mxp to mxn unit.
export const getCurrencyCode = (currencyCode: string): string =>
  currencyCode?.toLowerCase() === 'mxp' ? 'mxn' : currencyCode?.toLowerCase()

export const getCurrencyCodeFromProduct = (materialPricing): string =>
  (materialPricing?.length && materialPricing[0]?.currency
    ? materialPricing[0]?.currency
    : DEFAULT_CURRENCY_CODE
  ).toLowerCase()

export const mapPaymentMethod = (
  paymentMethod = '',
  isChargeback = false,
  contractNumber = '',
  isB2BUser = false
): string => {
  let paymentMethodString = ''
  switch (paymentMethod.toLowerCase()) {
    case 'cc':
      paymentMethodString = !!contractNumber
        ? PaymentMethodValues.cn
        : PaymentMethodValues.cc
      break

    case 'std':
      paymentMethodString = isChargeback
        ? PaymentMethodValues.chargeback
        : !!contractNumber
          ? PaymentMethodValues.cn
          : PaymentMethodValues.po
      break

    case 'mpower':
      paymentMethodString = PaymentMethodValues.mpower
      break

    case 'rapidred':
      paymentMethodString = PaymentMethodValues.rapidred
      break

    case 'cn':
      paymentMethodString = PaymentMethodValues.cn
      break

    default:
      paymentMethodString = isB2BUser
        ? PaymentMethodValues.b2b
        : PaymentMethodValues.others
      break
  }

  return paymentMethodString
}

/*
  Price formatter function for analytics where if the value isn't a valid number we set it to null, and all valid numbers are parsed to be a type of a float with two digits of percision.

  This is to fix some inconsistencies we had when setting fallback prices in the event a price was undefined.
  For undefined prices we always want null.

  And all other prices should get two digits of percision and be a float. Not a number as a string.
*/
export const priceFormatter = (
  priceValue: number | string | null | undefined
): number | null => {
  // null, undefined, and '' (empty string) are all falsey in javascript.
  // If priceValue is ever one of these we simply should immediately give back the null undefined value requested by the analytics team.
  if (!priceValue) {
    return null
  }

  // Try to parse input price Value to a number if it is defined.
  // Number gives back a NaN (falsey) value if it gets a string with things other than numeric characters and decimals. Which should return null for interface support with Google Analytics.
  const parsePrice = Number(priceValue)

  if (!parsePrice) {
    return null
  }

  // Try to parse the number to a float with two digits. May not need a try/catch but number parsing in javascript can behave oddly sometimes so better safe than sorry.
  try {
    return parseFloat(parsePrice.toFixed(2))
  } catch (err) {
    console.error('[Analytics] Error Formatting Price:', err)
    return null
  }
}

export const setPaClipData = (
  values: CartItemInput[],
  materialPricing
): PaClipData[] | undefined => {
  try {
    if (!values.length || !materialPricing.length) return
    const products = values?.map((product) => {
      const material = materialPricing?.find(
        (item) => item.materialNumber === product.materialNumber
      )

      return {
        id: (material?.product || EventValues.Empty).toLowerCase(),
        name: (material?.materialDescription || EventValues.Empty)
          .toLowerCase()
          ?.replace(regexForStripHTML, '')
          ?.replace(regexForStripEncodingUnicode, ''),
        variant: (material?.materialNumber || EventValues.Empty).toLowerCase(),
        brand: (material?.brand || EventValues.Empty).toLowerCase(),
        quantity: product?.quantity,
        price: priceFormatter(material?.price),
        availability: material.availabilities.find(
          (item) => item.messageType === 'primary'
        ),
        subscriptionFrequency: product?.subscriptionFrequency,
      }
    })
    return products.length ? products : []
  } catch (error) {
    console.error('ERROR - Add to cart Event ', error)
    return []
  }
}

export const setMaterialData = (
  values: CartItemInput[],
  favorite: any,
  pageName?: AddToCartPagesEnum,
  bundle?: {
    quantity: number
    bundleId: string
  }
): MaterialProductsData[] => {
  try {
    const itemGroups = groupBy(values || [], (item) => item.materialNumber)
    const priceGroups = groupBy(
      favorite || [],
      (item) => item.materialNumber || item?.material?.number
    )
    const products = Object.values(itemGroups).map((group, index) => {
      const item = group[0]

      let materialPricing = favorite?.find((elem) => {
        if (
          pageName === AddToCartPagesEnum.QuoteDetails ||
          pageName === AddToCartPagesEnum.QuickOrderQuotesModal ||
          pageName === AddToCartPagesEnum.AccountDashboardQuote
        ) {
          return elem?.materialInfo?.number === item?.materialNumber
        }

        if (pageName === AddToCartPagesEnum.PromoBundle) {
          return elem?.materialNumber === item?.materialNumber
        }

        return elem?.material?.number === item?.materialNumber
      })

      const avgGroupPrice = (
        Object.values(priceGroups)[index]?.reduce(
          (acc, item) =>
            (item?.material?.price ||
              item?.pricePerUnit ||
              item?.listPrice ||
              0) + acc,
          0
        ) / group.length
      )?.toFixed(2)

      if (
        pageName === AddToCartPagesEnum.QuoteDetails ||
        pageName === AddToCartPagesEnum.QuickOrderQuotesModal ||
        pageName === AddToCartPagesEnum.AccountDashboardQuote
      ) {
        materialPricing = {
          ...materialPricing,
          material: materialPricing?.materialInfo,
        }
      }

      return {
        id: (
          materialPricing?.material?.product ||
          materialPricing?.product ||
          materialPricing?.material?.number ||
          materialPricing?.materialNumber ||
          EventValues.Empty
        )?.toLowerCase(),
        name:
          (materialPricing?.type === ConfiguratorProductType.Configurator
            ? materialPricing?.material?.description ||
              materialPricing?.material?.name
            : materialPricing?.material?.name ||
              materialPricing?.material?.description ||
              materialPricing?.materialName
          )
            ?.replace(regexForStripEncodingUnicode, '')
            ?.replace(regexForStripHTML, '')
            ?.replace(regexForStripSymbols, '')
            ?.toLowerCase() || EventValues.Empty,
        variant: (
          materialPricing?.material?.number ||
          materialPricing?.materialNumber ||
          EventValues.Empty
        )?.toLowerCase(),
        brand: (
          materialPricing?.material?.brand?.key ||
          materialPricing?.material?.brand?.name ||
          materialPricing?.brand ||
          EventValues.Empty
        )?.toLowerCase(),
        coupon: bundle?.bundleId || EventValues.Empty,
        quantity: bundle
          ? bundle?.quantity * item?.quantity
          : group.reduce((acc, item) => acc + item?.quantity, 0) || 1,
        price: priceFormatter(avgGroupPrice),
        dimension91: 'standard',
        availability: item?.availability,
      }
    })
    return products ? products : []
  } catch (error) {
    console.error('ERROR - Add to cart Event ', error)
    return []
  }
}

export const clubbingUpProducts = (
  products,
  page?: string,
  dimension91?: string,
  isSubscription: boolean = false
): (GTMEcommerceItem | PromoProductItems)[] => {
  let flattenedProducts: any = []
  if (page === 'order_acknowledgement') {
    flattenedProducts = products.filter(
      (item) => item?.type === 'PromotionalBundle'
    )
  } else {
    flattenedProducts = products.flatMap((item) => item?.bundleItems)
  }

  // flattenedProducts =
  //   flattenedProducts.length &&
  //   flattenedProducts.map((item, index) => {
  //     item.bundleItem = index
  //     return item
  //   })
  const promoItems: any = groupBy(flattenedProducts || [], (item) => {
    return item?.material?.id
  })
  forEach(promoItems, (_value, key) => {
    promoItems[key] = groupBy(promoItems[key] || [], (item) => item?.promoCode)
  })
  const promoProducts = Object.values(promoItems).map((group: any) => {
    return Object.values(group).map((group1: any) => {
      const item = group1[0]
      const avgGroupPrice =
        group1.reduce(
          (acc, item) =>
            (item.pricing?.price ||
              item?.pricePerUnit ||
              item?.listPrice ||
              0) + acc,
          0
        ) / group1.length
      return {
        id: (item?.material?.product || EventValues.Empty)?.toLowerCase(),
        name:
          (item.material?.name || item.material?.description)
            ?.replace(regexForStripHTML, '')
            ?.replace(regexForStripEncodingUnicode, '')
            ?.toLowerCase() || EventValues.Empty,
        variant: item?.material?.number?.toLowerCase() || EventValues.Empty,
        brand: (item?.material?.brand?.key || EventValues.Empty)?.toLowerCase(),
        quantity: group1.reduce((acc, item) => acc + item?.quantity, 0) || 1,
        price: avgGroupPrice?.toString() || '0.00',
        coupon: item?.promoCode || EventValues.Empty,
        discount: item?.discount,
      }
    })
  })
  const flattenedProd = (promoProducts.length && promoProducts?.flat(1)) || []
  const nonBundleItems = products.filter(
    (item) => item?.type !== 'PromotionalBundle'
  )

  const itemGroups = groupBy(nonBundleItems || [], (item) => {
    if (item?.type === EventValues.Emprove) {
      return item?.emproveDossierInfo?.dossierMaterialNumber
    }
    const id = item?.material?.id || ''
    if (isSubscription) {
      const subscription = item?.subscriptionFrequency
      return subscription ? `${id}_${String(subscription)}` : id
    }
    return id
  })
  const productsArray: GTMEcommerceItem[] = Object.values(itemGroups).map(
    (group) => {
      const item = group[0]
      const avgGroupPrice =
        group.reduce(
          (acc, item) =>
            (item.pricing?.price ||
              item?.pricePerUnit ||
              item?.listPrice ||
              0) + acc,
          0
        ) / group.length
      if (item.type === EventValues.Emprove) {
        return {
          id: (item?.material?.id || EventValues.Empty).toLowerCase(),
          name: (
            (item.material.description
              ? item.material.description
              : item.material.name) || EventValues.Empty
          )
            .toLowerCase()
            .replace(regexForStripHTML, '')
            .replace(regexForStripEncodingUnicode, ''),
          variant: (
            item?.emproveDossierInfo?.dossierMaterialNumber || EventValues.Empty
          ).toLowerCase(),
          brand: (
            item?.material?.brand?.name || EventValues.SialBrand
          ).toLowerCase(),
          quantity: 1,
          price: priceFormatter(avgGroupPrice),
          coupon: item.promoCode || EventValues.Empty,
          dimension91: dimension91 ?? Dimension91.Standard,
          discount: item?.discount,
          ...(isSubscription && {
            subscriptionFrequency: item?.subscriptionFrequency,
          }),
        }
      } else {
        return {
          id: (item?.type === ConfiguratorProductType.Configurator
            ? item?.material?.number || EventValues.Empty
            : item?.material?.product || EventValues.Empty
          ).toLowerCase(),
          name: (
            (item?.type === ConfiguratorProductType.Configurator
              ? item.material?.description || item.material?.name
              : item.material?.name || item.material?.description) ||
            EventValues.Empty
          )
            .toLowerCase()
            .replace(regexForStripHTML, '')
            .replace(regexForStripEncodingUnicode, ''),
          variant: (item?.material?.number || EventValues.Empty).toLowerCase(),
          brand: (item?.type === ConfiguratorProductType.Configurator
            ? item?.material?.brand?.name || EventValues.Empty
            : item?.material?.brand?.key || EventValues.Empty
          ).toLowerCase(),
          quantity: group.reduce((acc, item) => acc + item.quantity, 0) || 1,
          price: priceFormatter(avgGroupPrice),
          coupon: item.promoCode || EventValues.Empty,
          dimension91: dimension91 ?? Dimension91.Standard,
          discount: item?.discount,
          ...(isSubscription && {
            subscriptionFrequency: item?.subscriptionFrequency,
          }),
        }
      }
    }
  )
  return [...productsArray, ...flattenedProd] || []
}
